import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface FloatingActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  label?: string;
  loading?: boolean;
}

/**
 * Mobile Floating Action Button (FAB)
 * Positioned fixed at bottom-right, hidden on desktop (md+)
 * Handles safe area insets for mobile devices with notches
 */
const FloatingActionButton = forwardRef<HTMLButtonElement, FloatingActionButtonProps>(
  ({ className, icon, label, loading, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          // Base styles
          'md:hidden fixed right-4 z-20',
          // Size and shape
          'h-14 rounded-full shadow-lg',
          // Padding: icon-only vs with label
          label ? 'px-5' : 'w-14',
          // Colors
          'bg-brand-500 text-white',
          // Interactive states
          'active:bg-brand-700 active:scale-95',
          'transition-all duration-150',
          // Flex layout
          'flex items-center justify-center gap-2',
          // Disabled state
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
          // Focus ring
          'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
          className
        )}
        style={{ bottom: 'calc(4.125rem + env(safe-area-inset-bottom))' }}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
        )}
        {label && <span className="font-semibold text-base">{label}</span>}
      </button>
    );
  }
);

FloatingActionButton.displayName = 'FloatingActionButton';

export { FloatingActionButton };
